import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  AppButton,
  AppDialog,
  AppTable,
  AppTableCell,
  AppTableRow,
  PageHeader,
  RowNumberCell,
  StageStatusChip,
  toast,
} from "../../../../components/ui";

import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";

import Box from "@mui/material/Box/Box";
import Paper from "@mui/material/Paper/Paper";
import Stack from "@mui/material/Stack/Stack";

import "../Leads.scss";
import TableActionMenu from "./LeadActionsMenu";
import {
  CategoryMapping,
  CSADisplayText,
  errorInstructions,
  LeadType,
  ReasonForNAEmpDisplayMapping,
  ReasonForPIEmpDisplayMapping,
} from "../shared/Constants";
import { getAttorneyStageLabel, formatAttorneyStageFilterCount, ATTORNEY_STAGE_FILTER_ORDER, getAttorneyStageCount } from "../shared/attorneyStageLabels";
import { buildCaseStatusFields, getCompactCaseStatusLabel } from "../LeadDetails/CaseStatusSummary";
import {
  LeadsActionsHeadCells,
  LeadsSelectAllHeadCell,
  mapLeadHeadCells,
} from "./LeadTableHeader";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { LeadsService } from "../../../../api/services/LeadsService";
import {
  AdminLeadStatus,
  ApplicationRole,
  ArchiveRequest,
  AttorneyApprovalStatus,
  AttorneyLeadStatus,
  Category,
  ClientRepresentationRequest,
  CSAType,
  DeclinedAction,
  GetUserListRequest,
  LeadShortResponse,
  LeadTableType,
  ReasonForNAEmp,
  ReasonForNAPI,
  ReasonForNgcWC,
  TableFilter,
  TableLeadsRequest,
  UpdateApprovalStatusRequest,
  UserShortResponse,
  UsersService,
} from "../../../../api";
import useNavigateSearch from "../../../../services/useNavigateSearch";
import { HelpersService } from "../../../../services/Helpers";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AllHead,
  Action,
  ActiveClientsHead,
  ArchivedHead,
  AttorneyReviewStatusText,
  DEFAULT_ROWS_PER_PAGE,
  IntakeHeadHead,
  LeadAttorneyStatusText,
  LeadData,
  LeadsState,
  ReferralHead,
  ReviewAdminHead,
  ReviewAttorneyHead,
  SharedAttorneyHead,
  SharedAttorneyHeadMobile,
  TableInteractionsProps,
} from "./LeadsTableConstants";
import { useDispatch, useSelector } from "react-redux";
import {
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { startSpinner, stopSpinner } from "../../../../redux/actions/spinnerActions";
import dayjs from "dayjs";
import { ArchiveInBulkRequest } from "../../../../api/models/ArchiveInBulkRequest";
import { MoveToReferInBulkRequest } from "../../../../api/models/MoveToReferInBulkRequest";
import type { LeadViewModel } from "../../../../api/models/LeadViewModel";
import { LeadsTableFilters } from "./LeadsFilters/LeadsTableFilters";
import {
  filtersFromTableFilters,
  mergeDrawerFilters,
  resetFilters,
} from "./LeadsFilters/filtering";
import type { LeadsFiltersState } from "./LeadsFilters/types";

const MySwal = withReactContent(Swal);

function resolveAttorneyStatusFromParam(
  param: string | null
): AttorneyLeadStatus | undefined {
  if (!param?.trim()) {
    return undefined;
  }

  const normalized = param.trim().toLowerCase();

  const byValue = Object.values(AttorneyLeadStatus).find(
    (status) =>
      typeof status === "string" && status.toLowerCase() === normalized
  );
  if (byValue) {
    return byValue as AttorneyLeadStatus;
  }

  const byKey =
    AttorneyLeadStatus[param.trim().toUpperCase() as keyof typeof AttorneyLeadStatus];
  return byKey ?? undefined;
}

function buildAttorneyLeadsFilters(
  tableType: LeadTableType,
  attorneyId: string | null,
  attorneyStatus: string | null
): TableFilter[] {
  if (tableType !== LeadTableType.SHARED_ATTORNEY) {
    return [];
  }

  const filters: TableFilter[] = [];

  if (attorneyId) {
    filters.push({
      filterField: "UserId",
      filterValue: attorneyId,
      isEnum: false,
    });
  }

  const status = resolveAttorneyStatusFromParam(attorneyStatus);
  if (status) {
    filters.push({
      filterField: "AttorneyStatus",
      filterValue: status,
      isEnum: true,
    });
  }

  return filters;
}

function attorneyStatusToUrlParam(status: AttorneyLeadStatus): string {
  const entry = Object.entries(AttorneyLeadStatus).find(
    ([key, value]) => value === status && key === key.toUpperCase()
  );
  return entry?.[0] ?? status;
}

function buildAttorneyLeadsPath(
  attorneyId: string | null,
  attorneyStatus: string | null
): string {
  const params = new URLSearchParams();
  if (attorneyId) {
    params.set("attorneyId", attorneyId);
  }
  if (attorneyStatus) {
    params.set("attorneyStatus", attorneyStatus);
  }
  const query = params.toString();
  return query ? `/attorney-leads?${query}` : "/attorney-leads";
}

interface LeadsTableProps {
  tableType: LeadTableType;
}

export default function LeadsTable(props: LeadsTableProps) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const attorneyIdParam = searchParams.get("attorneyId");
  const attorneyStatusParam = searchParams.get("attorneyStatus");
  const statusFilterParam = searchParams.get("status");

  const [locationPath, setLocationPath] = useState<string>();
  const [selected, setSelected] = useState<number[]>([]);
  const [visibleRows, setVisibleRows] = useState<LeadData[]>([]);
  const [leadsCount, setLeadsCount] = useState<Record<string, number> | null>();
  const [totalCount, setTotalCount] = useState(0);

  const [bulkEnabled, setBulkEnabled] = useState(false);
  const [openArchivedDialog, setOpenArchivedDialog] = useState(false);
  const [selectedArchiveType, setSelectedArchiveType] =
    useState<AdminLeadStatus>();

  const [archivedComment, setArchivedComment] = useState<string>();
  const [naReason, setNaReason] = useState<ReasonForNAEmp | ReasonForNAPI>();
  const [actionRow, setActionRow] = useState<LeadData>();
  const [isBulkArchive, setIsBulkArchive] = useState(false);
  const [bulkArchiveLeads, setBulkArchiveLeads] = useState<LeadViewModel[]>([]);

  const [tableInteractions, setTableInteractions] =
    useState<TableInteractionsProps>(() => ({
      sortAsc: false,
      sortBy: undefined,
      filters: buildAttorneyLeadsFilters(
        props.tableType,
        new URLSearchParams(window.location.search).get("attorneyId"),
        new URLSearchParams(window.location.search).get("attorneyStatus")
      ),
      page: 0,
      rowsPerPage: DEFAULT_ROWS_PER_PAGE,
    }));
  const [nameSearchValue, setNameSearchValue] = useState<string>();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [openReassignDialog, setOpenReassignDialog] = useState(false);
  const [openMoveToReferDialog, setOpenMoveToReferDialog] = useState(false);
  const [staffAttorneysList, setStaffAttorneysList] = useState<
    UserShortResponse[]
  >([]);
  const [selectedStaffAttorneyId, setSelectedStaffAttorneyId] =
    useState<number>();

  const [selectedReferStaffAttorneyId, setSelectedReferStaffAttorneyId] = useState<number>();
  const [selectedCSA, setSelectedCSA] = useState<CSAType>();
  const [CSAComment, setCSAComment] = useState<string>();
  const [selectedAttorneyName, setSelectedAttorneyName] = useState<string>("");
  const [attorneys, setAttorneys] = useState<UserShortResponse[]>([]);
  const [leadsFilters, setLeadsFilters] = useState<LeadsFiltersState>(resetFilters());
  const [leadCreators, setLeadCreators] = useState<UserShortResponse[]>([]);
  const [reviewingAttorneys, setReviewingAttorneys] = useState<UserShortResponse[]>([]);

  const { names, ids, selectedName } = (location.state as LeadsState) ?? {};

  const navigate = useNavigateSearch();
  const navigateState = useNavigate();
  const user = useSelector((state: any) => state.user?.user);
  const hideArchivedRejections =
    props.tableType === LeadTableType.SHARED_ATTORNEY &&
    !attorneyIdParam &&
    user?.roles?.includes(ApplicationRole.ATTORNEY) &&
    !user?.roles?.includes(ApplicationRole.ADMIN);
  const isViewingOwnAttorneyLeads = useMemo(() => {
    if (props.tableType !== LeadTableType.SHARED_ATTORNEY) {
      return false;
    }
    if (!attorneyIdParam) {
      return true;
    }
    return user?.id != null && String(user.id) === attorneyIdParam;
  }, [props.tableType, attorneyIdParam, user?.id]);
  const dispatch = useDispatch();

  const ATTORNEY_ROLES = [
    ApplicationRole.ADMIN,
    ApplicationRole.ATTORNEY,
    ApplicationRole.STAFF_ATTORNEY,
    ApplicationRole.STAFF,
    ApplicationRole.REFFERAL_STAFF,
    ApplicationRole.USER
  ];
  
  useEffect(() => {
    if (selectedName) {
      setSelectedAttorneyName(selectedName);
      return;
    }

    if (attorneyIdParam && ids?.length && names?.length) {
      const index = ids.findIndex((id) => String(id) === attorneyIdParam);
      if (index !== -1) {
        setSelectedAttorneyName(names[index]);
        return;
      }
    }

    if (attorneyIdParam && attorneys.length) {
      const attorney = attorneys.find((a) => String(a.id) === attorneyIdParam);
      if (attorney) {
        setSelectedAttorneyName(
          `${attorney.firstName ?? ""} ${attorney.lastName ?? ""}`.trim()
        );
      }
    }
  }, [selectedName, attorneyIdParam, ids, names, attorneys]);

  useEffect(() => {
    async function loadAttorneys() {
      const requests = ATTORNEY_ROLES.map(role =>
        UsersService.postApiUsersGetUsersByRole({
          role,
          addAdmins: true
        } as GetUserListRequest)
      );
  
      const results = await Promise.all(requests);
  
      const combined = results.flat();
      const unique = Array.from(new Map(combined.map(u => [u.id, u])).values());
  
      const sorted = unique.sort((a, b) => {
        const firstA = a.firstName?.toLowerCase() ?? "";
        const firstB = b.firstName?.toLowerCase() ?? "";
        const lastA = a.lastName?.toLowerCase() ?? "";
        const lastB = b.lastName?.toLowerCase() ?? "";
  
        return firstA === firstB
          ? lastA.localeCompare(lastB)
          : firstA.localeCompare(firstB);
      });

      setAttorneys(sorted);
    }
  
    loadAttorneys();
  }, []);

  useEffect(() => {
    setIsMobile(HelpersService.checkMobile());
    if (user.roles.includes(ApplicationRole.ADMIN)) {
      UsersService.postApiUsersGetUsersByRole({
        role: ApplicationRole.STAFF_ATTORNEY,
        category: Category.EMPLOYMENT,
        addAdmins: true
      } as GetUserListRequest
      ).then((attorneys) => {
        setStaffAttorneysList(attorneys);
      });
    }
  }, []);

  useEffect(() => {
  const key = location.pathname + location.search;
  if (locationPath === key) return;

  setLocationPath(key);

  setTableInteractions(prev => ({
    ...prev,
    page: 0,
  }));

  setVisibleRows([]);
  setSelected([]);
}, [location.pathname, location.search]);

  useEffect(() => {
    if (props.tableType !== LeadTableType.ARCHIVE || !statusFilterParam) {
      return;
    }

    const statusFromQuery = Object.values(AdminLeadStatus).find(
      (status) => status.toLowerCase() === statusFilterParam.toLowerCase()
    );

    if (statusFromQuery) {
      handleChangeFilter(statusFromQuery);
    }
  }, [props.tableType, statusFilterParam]);

  useEffect(() => {
    if (props.tableType !== LeadTableType.SHARED_ATTORNEY) {
      return;
    }

    const urlFilters = buildAttorneyLeadsFilters(
      props.tableType,
      attorneyIdParam,
      attorneyStatusParam
    );

    setTableInteractions((prev) => {
      const nonUrlFilters =
        prev.filters?.filter(
          (filter) =>
            filter.filterField !== "UserId" &&
            filter.filterField !== "AttorneyStatus"
        ) ?? [];

      return {
        ...prev,
        filters: [...nonUrlFilters, ...urlFilters],
        page: 0,
      };
    });
  }, [props.tableType, attorneyIdParam, attorneyStatusParam]);


  const syncAttorneyStageToUrl = (status: string | null) => {
    const statusParam = status
      ? attorneyStatusToUrlParam(status as AttorneyLeadStatus)
      : null;

    const nextState =
      names?.length && ids?.length
        ? { names, ids, selectedName: selectedAttorneyName || selectedName }
        : location.state;

    navigateState(buildAttorneyLeadsPath(attorneyIdParam, statusParam), {
      state: nextState,
    });
  };

  const handleAttorneySelect = (name: string) => {
    setSelectedAttorneyName(name);
  
    const index = names.indexOf(name);
    if (index === -1) return;
  
    const id = ids[index];
  
    navigateState(buildAttorneyLeadsPath(String(id), attorneyStatusParam), {
      state: { names, ids, selectedName: name },
    });
  };

  const applyLeadsFilters = (next: LeadsFiltersState) => {
    setLeadsFilters(next);
    setTableInteractions((prev) => ({
      ...prev,
      filters: mergeDrawerFilters(prev.filters, next),
      page: 0,
    }));
  };

  const clearLeadsFilters = () => {
    applyLeadsFilters(resetFilters());
  };

  useEffect(() => {
    setLeadsFilters(
      filtersFromTableFilters(
        tableInteractions.filters,
        leadCreators,
        reviewingAttorneys
      )
    );
    // Sync drawer label state once option lists load / chip Status changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadCreators, reviewingAttorneys, tableInteractions.filters]);

  const initiateLeads = async () => {
    dispatch(startSpinner());

    var request = {
      type: props.tableType,
      limit: tableInteractions.rowsPerPage,
      offset: tableInteractions.rowsPerPage * tableInteractions.page,
      sortBy: tableInteractions.sortBy,
      sortAscending: tableInteractions.sortAsc,
      filters: tableInteractions.filters
    } as TableLeadsRequest;
    LeadsService.postApiLeadsLeads(request).then(response => {
      let rowsOnMount = [] as LeadData[];
      if (!!response.leads) {
        rowsOnMount = response.leads.map((row) => {
          let action = { actions: getActions(row) };
          return { ...(row as LeadData), ...action };
        });
      }

      setVisibleRows(rowsOnMount);
      setTotalCount(response.totalCount ?? 0);
      setLeadsCount(response.countByStatus);
    }).finally(() => dispatch(stopSpinner()));
  };

  useEffect(() => {
    initiateLeads();
  }, [tableInteractions]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    newSortBy: keyof LeadShortResponse
  ) => {
    const toggledOrder =
      tableInteractions.sortBy !== newSortBy
        ? true
        : !tableInteractions.sortAsc;
    const newTableInteractions = {
      ...tableInteractions,
      sortAsc: toggledOrder,
      sortBy: newSortBy,
      page: 0,
    };
    setTableInteractions(newTableInteractions);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    const newTableInteractions = { ...tableInteractions, page: newPage };
    setTableInteractions(newTableInteractions);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newTableInteractions = {
      ...tableInteractions,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    };
    setTableInteractions(newTableInteractions);
  };

  const handleChangeFilter = (
    value: string | null = null,
    field: string = "Status",
    isEnum: boolean = true
  ) => {
    if (
      field === "AttorneyStatus" &&
      props.tableType === LeadTableType.SHARED_ATTORNEY
    ) {
      syncAttorneyStageToUrl(value);
      return;
    }

    var oldFilters = tableInteractions.filters ?? [];
    if (field !== "Status") {
      oldFilters = oldFilters.filter((f) => f.filterField !== "Status");
    }
    const existingFilterIndex = oldFilters.findIndex(
      (filter) => filter.filterField === field
    );

    if (existingFilterIndex !== -1) {
      const updatedFilters = [...oldFilters];
      updatedFilters[existingFilterIndex] = {
        filterField: field,
        filterValue: value ?? "",
        isEnum: isEnum
      } as TableFilter;
      setTableInteractions((prevState) => ({
        ...prevState,
        filters: updatedFilters,
        page: 0,
      }));
    } else {
      setTableInteractions((prevState) => ({
        ...prevState,
        filters: [
          ...oldFilters,
          {
            filterField: field,
            filterValue: value ?? "",
            isEnum: isEnum,
          } as TableFilter,
        ],
        page: 0,
      }));
    }
  };

  const updateSearch = (value: string) => {
    setNameSearchValue(value);
    if (value.length > 2 || value.length === 0) {
      handleChangeFilter(value, "LeadName", false);
    }
  };

  const isDoneFilterActive = () => {
    const statusFilter = (tableInteractions.filters ?? []).find(
      (filter) => filter.filterField === "Status"
    );

    return (
      statusFilter?.filterValue?.toString().toLowerCase() ===
      AdminLeadStatus.DONE.toLowerCase()
    );
  };

  const isBulkArchiveMode = () =>
    props.tableType === LeadTableType.ACTIVE_CLIENT &&
    isDoneFilterActive() &&
    !!user?.roles?.includes(ApplicationRole.ADMIN);

  useEffect(() => {
    if (!isBulkArchiveMode()) {
      setSelected([]);
      setIsBulkArchive(false);
      setBulkArchiveLeads([]);
    }
  }, [tableInteractions.filters, props.tableType, user?.roles]);

  const isSelected = (index: number) => selected.indexOf(index) !== -1;
  const handleRowClick = (row: LeadData) => {
    const selectedIndex = selected.indexOf(row.id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = selected.concat(row.id);
    } else if (selectedIndex === 0) {
      newSelected = selected.slice(1);
    } else if (selectedIndex === selected.length - 1) {
      newSelected = selected.slice(0, -1);
    } else if (selectedIndex > 0) {
      newSelected = selected
        .slice(0, selectedIndex)
        .concat(selected.slice(selectedIndex + 1));
    }

    calculateBulkActionState(newSelected);
    setSelected(newSelected);
  };

  const fetchAllMatchingLeadIds = async (): Promise<number[]> => {
    const request = {
      type: props.tableType,
      limit: totalCount || 999999,
      offset: 0,
      sortBy: tableInteractions.sortBy,
      sortAscending: tableInteractions.sortAsc,
      filters: tableInteractions.filters,
    } as TableLeadsRequest;
    const response = await LeadsService.postApiLeadsLeads(request);
    return response.leads?.map((lead) => lead.id!) ?? [];
  };

  const handleSelectAllClick = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (isBulkArchiveMode()) {
      if (event.target.checked) {
        dispatch(startSpinner());
        try {
          const allIds = await fetchAllMatchingLeadIds();
          setSelected(allIds);
        } finally {
          dispatch(stopSpinner());
        }
      } else {
        setSelected([]);
      }
      return;
    }

    setBulkEnabled(false);
    if (event.target.checked) {
      const newSelected = visibleRows.map((n) => n.id);
      setSelected(newSelected);
      calculateBulkActionState(newSelected ?? []);
      return;
    }
    setSelected([]);
  };

  const calculateBulkActionState = (newSelected: number[]) => {
    const selectedRows = visibleRows.filter((row) =>
      newSelected.includes(row.id)
    );
    setBulkEnabled(
      selectedRows.length > 0 &&
      selectedRows.filter(
        (row) =>
          ( (row.status === AdminLeadStatus.NEW || row.status === AdminLeadStatus.REJECTED) && row.attorneyApprovalStatus === AttorneyApprovalStatus.REFER )
      ).length === selectedRows.length
    );
  };

  const handleAddNewLead = () => {
    navigate(`/lead/new`);
  };

  const handleDetailsClick = (
    e: React.MouseEvent<unknown> | null,
    row: LeadData
  ) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (props.tableType === LeadTableType.SHARED_ATTORNEY) {
      navigate(`/lead/${row.leadId}`, {
        leadType: LeadType.Attorney,
        isEdit: "false",
      });
      return;
    }
    if (props.tableType === LeadTableType.REVIEW && (user.roles.includes(ApplicationRole.STAFF_ATTORNEY) || user.roles.includes(ApplicationRole.REFFERAL_STAFF))) {
      navigate(`/lead/${row.id}`, {
        leadType: LeadType.StaffAttorney,
        isEdit: "false",
      });
      return;
    }
    navigate(`/lead/${row.id}`, {
      isEdit: row.status === AdminLeadStatus.DRAFT,
    });
  };

  const setSelectWinner = (row: LeadData) => {
    navigate(`/lead/${row.id}`);
  };

  const handleArchiveLead = (row: LeadData) => {
    setIsBulkArchive(false);
    setBulkArchiveLeads([]);
    setActionRow(row);
    setOpenArchivedDialog(true);
  };

  const handleBulkArchiveClick = () => {
    if (!selected.length) {
      return;
    }

    dispatch(startSpinner());
    LeadsService.postApiLeadsGetByIds({ ids: selected })
      .then((leads) => {
        setBulkArchiveLeads(leads);
        setIsBulkArchive(true);
        setActionRow(undefined);
        setSelectedArchiveType(undefined);
        setArchivedComment(undefined);
        setNaReason(undefined);
        setOpenArchivedDialog(true);
      })
      .catch(() => {
        toast.error(errorInstructions || "Error loading selected leads");
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const handleRestoreLead = (id: number) => {
    LeadsService.postApiLeadsRestore(id)
      .then(() => {
        toast.success("Success!");
        initiateLeads();
      })
      .catch(() => {
        toast.error(errorInstructions || "Error restoring lead");
      });
  };

  const handleDeleteIncomplete = (row: LeadData) => {
    MySwal.fire({
      title: <strong>Confirm lead deletion</strong>,
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "No, go back",
      confirmButtonText: "Yes, delete",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
    })
      .then((result) => {
        if (result.isConfirmed)
          LeadsService.deleteApiLeads(row.id).then(() => {
            toast.success("Lead deleted successfully!");
        initiateLeads();
          });
      })
      .catch(() => {
        toast.error(errorInstructions || "Error deleting lead");
      });
  };

  const handleUnassign = (row: LeadData) => {
    MySwal.fire({
      title: <strong>Confirm unassignment</strong>,
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "No, go back",
      confirmButtonText: "Yes, remove",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
    })
      .then((result) => {
        if (result.isConfirmed)
          LeadsService.postApiLeadsUnassign(row.id).then(() => {
            toast.success("Lead unassigned successfully!");
        initiateLeads();
          });
      })
      .catch(() => {
        toast.error(errorInstructions || "Error unassigning lead");
      });
  };

  const handleReassignLead = (row: LeadData) => {
    setActionRow(row);
    setOpenReassignDialog(true);
  };

  const sendLead = (row: LeadData) => {
    navigate(`/lead/send/${row.id}`);
  };

  const getActions = (row: LeadShortResponse): Action[] => {
    var actions: Action[] = [];
    if (props.tableType === LeadTableType.REVIEW) {
      actions = [{ name: "Unassign", function: handleUnassign }];
      if (user?.roles?.includes(ApplicationRole.ADMIN)) {
        if (row.attorneyApprovalStatus === AttorneyApprovalStatus.IN_PROCESS || row.attorneyApprovalStatus === AttorneyApprovalStatus.MORE_INFO_PROVIDED || row.attorneyApprovalStatus === AttorneyApprovalStatus.MORE_INFO_REQUESTED) {
          actions = [...actions, { name: "Reassign", function: handleReassignLead }];
        }
        actions = [...actions, { name: "Archive", function: handleArchiveLead }];
      }
      return actions;
    }

    switch (row.status) {
      //Intake process
      case AdminLeadStatus.DRAFT: {
            if (
                user?.roles?.includes(ApplicationRole.ADMIN) || 
                user?.roles?.includes(ApplicationRole.STAFF)
            ) {
          return [{ name: "Delete", function: handleDeleteIncomplete }];
        }
        break;
      }
      //Referral process
      case AdminLeadStatus.REJECTED:
      case AdminLeadStatus.NEW: {
        if (row.attorneyApprovalStatus === AttorneyApprovalStatus.REFER) {
          actions = [{ name: "Send lead", function: sendLead }];
        }
        if (row.attorneyApprovalStatus === AttorneyApprovalStatus.KEEP) {
          actions = [{ name: "Move to Refer", function: handleClientDenial }];
        }
        break;
      }
      case AdminLeadStatus.SHARED: {
        actions = [
          { name: "Select winner", function: setSelectWinner },
          { name: "Reshare", function: sendLead }
        ];
        break;
      }
      case AdminLeadStatus.INTRODUCTION: {
        actions = [
          { name: "Accept intake", function: () => { } },
          { name: "Reject intake", function: () => { } },
        ];
        break;
      }
      case AdminLeadStatus.WINNER_SELECTED: {
        actions = [{ name: "Reject", function: () => { } }];
        break;
      }
      case AdminLeadStatus.NGC:
      case AdminLeadStatus.N_A:
      case AdminLeadStatus.CSADECLINED:
      case AdminLeadStatus.ATTORNEY_REJECT:
      case AdminLeadStatus.CLIENT_REFERED:
      case AdminLeadStatus.CLIENT_RETAINED: {
        if (props.tableType === LeadTableType.ARCHIVE) {
          return [
            {
              name: "Restore lead",
              function: () => {
                handleRestoreLead(row.id);
              },
            },
          ];
        }
        return [];
      }
    }
    if (user?.roles?.includes(ApplicationRole.ADMIN)) {
      if (row.attorneyApprovalStatus === AttorneyApprovalStatus.IN_PROCESS || row.attorneyApprovalStatus === AttorneyApprovalStatus.MORE_INFO_PROVIDED || row.attorneyApprovalStatus === AttorneyApprovalStatus.MORE_INFO_REQUESTED) {
        actions = [...actions, { name: "Reassign", function: handleReassignLead }];
      }
      actions = [...actions, { name: "Archive", function: handleArchiveLead }];
    }
    return actions;
  };

  const generateTableHeadCells = () => {
    switch (props.tableType) {
      case LeadTableType.ACTIVE_CLIENT: {
        return ActiveClientsHead;
      }
      case LeadTableType.ALL: {
        return AllHead;
      }
      case LeadTableType.INTAKE: {
        return IntakeHeadHead;
      }
      case LeadTableType.REVIEW: {
        return user?.roles?.includes(ApplicationRole.ADMIN)
          ? ReviewAdminHead
          : ReviewAttorneyHead;
      }
      case LeadTableType.ARCHIVE: {
        return ArchivedHead;
      }
      case LeadTableType.REFERRAL: {
        return ReferralHead;
      }
      case LeadTableType.SHARED_ATTORNEY: {
        return isMobile ? SharedAttorneyHeadMobile : SharedAttorneyHead;
      }
    }
    return [];
  };

  const generateTableRow = (row: LeadData, index: number) => {
    let createdDate = HelpersService.calcDatesDifference(
      dayjs.utc(row.createdAt ?? "")
    );
    const rowNumber =
      tableInteractions.page * tableInteractions.rowsPerPage + index + 1;
    const numberCell = <RowNumberCell value={rowNumber} />;
    let sharedCells = (
      <>
        <AppTableCell align="left">{row.leadName}</AppTableCell>
        <AppTableCell align="left">{CategoryMapping[row.category]}</AppTableCell>
        <AppTableCell align="left">{HelpersService.formatTerminationDate(row.terminationDate)}</AppTableCell>
      </>
    );
    const caseStatusTooltip = row.referralCaseProgress
      ? buildCaseStatusFields(row.referralCaseProgress, (d) =>
          d ? dayjs(d).format("M/D/YYYY") : null
        )
          .filter((f) => f.value)
          .map((f) => `${f.label}: ${f.value}`)
          .join("\n")
      : undefined;
    let sharedAttorneyCells = (
      <>
        <AppTableCell align="left">{row.leadName}</AppTableCell>
        <AppTableCell align="left">{CategoryMapping[row.category]}</AppTableCell>
        <AppTableCell align="left" sx={{ maxWidth: 240 }}>
          <Tooltip
            title={caseStatusTooltip || "No case status recorded"}
            placement="top-start"
            enterDelay={400}
          >
            <Typography variant="body2" noWrap component="span">
              {getCompactCaseStatusLabel(row.referralCaseProgress)}
            </Typography>
          </Tooltip>
        </AppTableCell>
        <AppTableCell align="left">{HelpersService.formatTerminationDate(row.terminationDate)}</AppTableCell>
      </>
    );
    let intakeSharedCells = (
      <>
        <AppTableCell align="left">{row.reviewingAttorneyName}</AppTableCell>
        <AppTableCell align="left">{row.creatorName}</AppTableCell>
        <AppTableCell align="left">{createdDate}</AppTableCell>
      </>
    );

    let actionsCells = (
      <>
        <AppTableCell align="left">
          <AppButton
            variant="outlined"
            color="primary"
            size="small"
            onClick={(e) => handleDetailsClick(e, row)}
          >
            View details
          </AppButton>
        </AppTableCell>
        {!user?.roles?.includes(ApplicationRole.ATTORNEY) && (
          <AppTableCell align="left">
            {row.actions.length > 0 && (
              <TableActionMenu
                row={row}
                actions={row.actions}
              ></TableActionMenu>
            )}
          </AppTableCell>
        )}
      </>
    );

    switch (props.tableType) {
      case LeadTableType.ALL: {
        return (
          <AppTableRow
            hover
            tabIndex={-1}
            key={row.id}
            className={row.highlight ? "table-row-accent" : ""}
          >
            {numberCell}
            <AppTableCell align="left">
              <StageStatusChip status={row.status ?? AdminLeadStatus.NEW} />
            </AppTableCell>
            {sharedCells}
            <AppTableCell align="left">{row.reviewingAttorneyName}</AppTableCell>
            <AppTableCell align="left">{row.creatorName}</AppTableCell>
            <AppTableCell align="left">{createdDate}</AppTableCell>
            {actionsCells}
          </AppTableRow>
        );
      }
      case LeadTableType.ACTIVE_CLIENT: {
        let dateSinceContractSent = HelpersService.calcDatesDifference(
          dayjs.utc(row.contractSentDate ?? "")
        );

        let dateSinceUpdated = HelpersService.calcDatesDifference(
          dayjs.utc(row.updatedAt ?? "")
        );

        const bulkArchiveMode = isBulkArchiveMode();
        const isItemSelected = isSelected(row.id);
        const labelId = `enhanced-table-checkbox-${index}`;

        return (
          <AppTableRow
            hover
            tabIndex={-1}
            key={row.id}
            onClick={bulkArchiveMode ? () => handleRowClick(row) : undefined}
            role={bulkArchiveMode ? "checkbox" : undefined}
            aria-checked={bulkArchiveMode ? isItemSelected : undefined}
            selected={bulkArchiveMode ? isItemSelected : undefined}
            className={row.highlight ? "table-row-accent" : ""}
          >
            {bulkArchiveMode && (
              <AppTableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  checked={isItemSelected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => handleRowClick(row)}
                  inputProps={{
                    "aria-labelledby": labelId,
                  }}
                />
              </AppTableCell>
            )}
            {numberCell}
            <AppTableCell align="left">
              <StageStatusChip status={row.status ?? AdminLeadStatus.NEW} />
            </AppTableCell>
            {sharedCells}
            {intakeSharedCells}
            <AppTableCell align="left">
              {row.contractSentDate ? dateSinceContractSent : "Not sent yet"}
            </AppTableCell>
            <AppTableCell align="left">{row.supplementalAssignedName}</AppTableCell>
            <AppTableCell align="left">
              {row.updatedAt ? dateSinceUpdated : ""}
            </AppTableCell>
            {actionsCells}
          </AppTableRow>
        );
      }
      case LeadTableType.INTAKE: {
        let updatedDate = HelpersService.calcDatesDifference(dayjs.utc(row.updatedAt ?? ""));

        return (
          <AppTableRow
            hover
            tabIndex={-1}
            key={row.id}
            className={
              row.attorneyApprovalStatus ===
                AttorneyApprovalStatus.MORE_INFO_REQUESTED
                ? "table-row-accent"
                : ""
            }
            sx={{ cursor: "pointer" }}
          >
            {numberCell}
            {row.status === AdminLeadStatus.DRAFT ? (
              <AppTableCell align="left">
                <StageStatusChip
                  status={AdminLeadStatus.DRAFT}
                  label="Incomplete"
                />
              </AppTableCell>
            ) : (
              <AppTableCell align="left">
                <StageStatusChip
                  status={
                    row.attorneyApprovalStatus ?? AdminLeadStatus.NEW
                  }
                  label={
                    LeadAttorneyStatusText[
                      HelpersService.getEnumKeyByEnumValue(
                        AttorneyApprovalStatus,
                        row.attorneyApprovalStatus
                      )
                    ]
                  }
                />
              </AppTableCell>
            )}
            {sharedCells}
            {intakeSharedCells}
            <AppTableCell align="left">{updatedDate}</AppTableCell>
            {actionsCells}
          </AppTableRow>
        );
      }
      case LeadTableType.REVIEW: {
        return (
          <AppTableRow
            className={
              row.attorneyApprovalStatus ===
                AttorneyApprovalStatus.MORE_INFO_PROVIDED
                ? "table-row-accent"
                : ""
            }
            tabIndex={-1}
            key={row.id}
            hover
          >
            {numberCell}
            <AppTableCell align="left">
              <StageStatusChip
                status={
                  row.attorneyApprovalStatus ?? AdminLeadStatus.NEW
                }
                label={
                  AttorneyReviewStatusText[
                    HelpersService.getEnumKeyByEnumValue(
                      AttorneyApprovalStatus,
                      row.attorneyApprovalStatus
                    )
                  ]
                }
              />
            </AppTableCell>
            {sharedCells}
            {user?.roles?.includes(ApplicationRole.ADMIN) && (
              <>
                <AppTableCell align="left">{row.creatorName}</AppTableCell>
                <AppTableCell align="left">{row.reviewingAttorneyName}</AppTableCell>
              </>
            )}
            <AppTableCell align="left">{createdDate}</AppTableCell>
            {actionsCells}
          </AppTableRow>
        );
      }
      case LeadTableType.ARCHIVE: {
        const isItemSelected = isSelected(row.id);
        const labelId = `enhanced-table-checkbox-${index}`;

        return (
          <AppTableRow 
            hover
            onClick={() => handleRowClick(row)}
            role="checkbox"
            aria-checked={isItemSelected}
            tabIndex={-1}
            key={row.id}
            selected={isItemSelected}
            className={row.hasActivity ? "table-row-accent" : ""}
          >
            <AppTableCell padding="checkbox">
              <Checkbox
                color="primary"
                checked={isItemSelected}
                inputProps={{
                  "aria-labelledby": labelId,
                }}
              />
            </AppTableCell>
            {numberCell}
            <AppTableCell align="left">
              <StageStatusChip status={row.status} />
            </AppTableCell>
            {sharedCells}
            <AppTableCell align="left">{row.attorney}</AppTableCell>
            <AppTableCell align="left">
              {HelpersService.camelToString(row.archivedReason)}
            </AppTableCell>
            <AppTableCell align="left">{createdDate}</AppTableCell>
            {actionsCells}
          </AppTableRow>
        );
      }
      case LeadTableType.REFERRAL: {
        const isItemSelected = isSelected(row.id);
        const labelId = `enhanced-table-checkbox-${index}`;
        let updatedDate = HelpersService.calcDatesDifference(
          dayjs.utc(row.updatedAt ?? "")
        );
        return (
          <AppTableRow
            hover
            onClick={() => handleRowClick(row)}
            role="checkbox"
            aria-checked={isItemSelected}
            tabIndex={-1}
            key={row.id}
            selected={isItemSelected}
            className={row.hasActivity ? "table-row-accent" : ""}
          >
            <AppTableCell padding="checkbox">
              <Checkbox
                color="primary"
                checked={isItemSelected}
                inputProps={{
                  "aria-labelledby": labelId,
                }}
              />
            </AppTableCell>
            {numberCell}
            <AppTableCell align="left" className="table-cell">
              <StageStatusChip
                status={row.status ?? AdminLeadStatus.NEW}
                label={`${HelpersService.camelToString(row.status)}${
                  row.hasActivity && row.status === AdminLeadStatus.SHARED
                    ? " (accepted)"
                    : ""
                }`}
              />
            </AppTableCell>
            {sharedCells}
            <AppTableCell align="left">{row.attorney}</AppTableCell>
            <AppTableCell align="left">{row.creatorName}</AppTableCell>
            <AppTableCell align="left">{createdDate}</AppTableCell>
            <AppTableCell align="left">{updatedDate}</AppTableCell>
            {actionsCells}
          </AppTableRow>
        );
      }
      case LeadTableType.SHARED_ATTORNEY: {
        const isItemSelected = isSelected(row.id);
        const labelId = `enhanced-table-checkbox-${index}`;
        let updatedDate = HelpersService.calcDatesDifference(
          dayjs.utc(row.updatedAt ?? "")
        );
        return (
          <AppTableRow
            hover
            onClick={() => handleRowClick(row)}
            role="checkbox"
            aria-checked={isItemSelected}
            tabIndex={-1}
            key={row.id}
            selected={isItemSelected}
            className={row.hasActivity ? "table-row-accent" : ""}
          >
            <AppTableCell padding="checkbox">
              <Checkbox
                color="primary"
                checked={isItemSelected}
                inputProps={{
                  "aria-labelledby": labelId,
                }}
              />
            </AppTableCell>
            {numberCell}
            <AppTableCell align="left" className="table-cell">
              <StageStatusChip
                status={row.attorneyStatus ?? AttorneyLeadStatus.NEW}
                label={getAttorneyStageLabel(
                  row.attorneyStatus ?? AttorneyLeadStatus.NEW,
                  row.withoutPayment
                )}
              />
            </AppTableCell>
            {sharedAttorneyCells}
            <AppTableCell align="left">{updatedDate}</AppTableCell>
            {actionsCells}
          </AppTableRow>
        );
      }
      /*case LeadTableType.SHARED_ATTORNEY: {
        return (
          <AppTableRow tabIndex={-1} key={row.id}>
            <AppTableCell
              align="left"
              onClick={(e) => handleDetailsClick(e, row)}
              className="table-cell"
            >
              <AppButton
                variant="contained"
                color={StageColorsMapping[row.attorneyStatus!]}
                size="small"
                className={
                  row.withoutPayment !== true && generateAttorneyAction(row.attorneyStatus).length > 0
                    ? "table-btn-higher"
                    : ""
                }
              >
                {row.attorneyStatus}{row.withoutPayment !== true ? generateAttorneyAction(row.attorneyStatus) : ''}
              </AppButton>
            </AppTableCell>
            <AppTableCell align="left">{row.leadName ?? "Hidden"}</AppTableCell>
            <AppTableCell align="left" onClick={(e) => handleDetailsClick(e, row)}>
              {HelpersService.camelToString(row.category)}
            </AppTableCell>
            {!isMobile && (
              <>
                <AppTableCell align="left">
                  {HelpersService.calcDatesDifference(dayjs.utc(row.updatedAt ?? "")
                  )}
                </AppTableCell>
                <AppTableCell></AppTableCell>
                {actionsCells}
              </>
            )}
          </AppTableRow>
        );
      }*/
    }
  };

  const generateAttorneyAction = (status) => {
    switch (status) {
      case AttorneyLeadStatus.ACCEPTED: return " (await GLio confirmation)";
      case AttorneyLeadStatus.WON: return " (sign agreement)";
      case AttorneyLeadStatus.SIGNED: return " (pay fee)";
    }
    return "";
  }

  const generateTitle = () => {
    switch (props.tableType) {
      case LeadTableType.ACTIVE_CLIENT: {
        return "Active clients";
      }
      case LeadTableType.ALL: {
        return "All leads";
      }
      case LeadTableType.INTAKE: {
        return "New leads";
      }
      case LeadTableType.REVIEW: {
        return "Leads in review";
      }
      case LeadTableType.ARCHIVE: {
        return "Archived leads";
      }
      case LeadTableType.REFERRAL: {
        return "Referral leads";
      }
      case LeadTableType.SHARED_ATTORNEY: {
        if (!isViewingOwnAttorneyLeads) {
          const name = selectedAttorneyName?.trim();
          return name ? `${name}'s leads` : "Attorney leads";
        }
        return "My leads";
      }
    }
  };

  const generateSubtitle = () => {
    switch (props.tableType) {
      case LeadTableType.ACTIVE_CLIENT:
        return "Clients in contract and supplemental stages.";
      case LeadTableType.ALL:
        return "Search and filter across every lead.";
      case LeadTableType.INTAKE:
        return "New intakes waiting for review and next steps.";
      case LeadTableType.REVIEW:
        return "Leads currently under attorney or staff review.";
      case LeadTableType.ARCHIVE:
        return "Closed, declined, and retained leads.";
      case LeadTableType.REFERRAL:
        return "Shared referrals and winner selection progress.";
      case LeadTableType.SHARED_ATTORNEY:
        if (!isViewingOwnAttorneyLeads) {
          const name = selectedAttorneyName?.trim();
          return name
            ? `Shared leads assigned to ${name}.`
            : "Shared leads for the selected attorney.";
        }
        return "Leads shared with you for review.";
      default:
        return undefined;
    }
  };

  const getActiveStageFilter = (field: string = "Status") => {
    if (
      field === "AttorneyStatus" &&
      props.tableType === LeadTableType.SHARED_ATTORNEY
    ) {
      return resolveAttorneyStatusFromParam(attorneyStatusParam) ?? null;
    }
    const statusFilter = (tableInteractions.filters ?? []).find(
      (filter) => filter.filterField === field
    );
    const value = statusFilter?.filterValue?.toString();
    return value ? value : null;
  };

  const renderStageFilterStrip = (children: ReactNode) => (
    <Paper
      variant="outlined"
      className="stage-filter-strip"
      sx={{ px: 1.5, py: 1, mb: 2, bgcolor: "#fafbfd" }}
    >
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
        className="table-filters"
      >
        {children}
      </Stack>
    </Paper>
  );

  const isStageSelected = (
    status: string | null,
    field: string = "Status"
  ) => {
    const active = getActiveStageFilter(field);
    if (!status) {
      return !active;
    }
    return active?.toLowerCase() === status.toLowerCase();
  };

  const renderAttorneyFilter = () => {
    if (!names?.length) {
      return null;
    }

    return (
      <FormControl size="small" sx={{ minWidth: 180, mr: 1 }}>
        <InputLabel id="attorney-filter-label">Attorney</InputLabel>
        <Select
          labelId="attorney-filter-label"
          label="Attorney"
          value={selectedAttorneyName || ""}
          onChange={(e) => handleAttorneySelect(e.target.value)}
        >
          {names.map((name, idx) => (
            <MenuItem key={idx} value={name}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderAttorneyStageFilters = (totalAttorneyLeads: number) => {
    const active = getActiveStageFilter("AttorneyStatus");
    const visibleStages = ATTORNEY_STAGE_FILTER_ORDER.filter(
      (status) =>
        status !== AttorneyLeadStatus.REJECTED || !hideArchivedRejections
    );

    return (
    <>
      {isMobile ? (
        <FormControl size="small" className="min-width-200" sx={{ minWidth: 200 }}>
          <InputLabel id="type-label">Stage</InputLabel>
          <Select
            labelId="type-label"
            label="Stage"
            value={
              resolveAttorneyStatusFromParam(attorneyStatusParam) ?? 999
            }
            onChange={(e) => {
              handleChangeFilter(
                +e.target.value === 999 ? null : (e.target.value as string),
                "AttorneyStatus"
              );
            }}
          >
            <MenuItem key={999} value={999}>
              All({totalAttorneyLeads})
            </MenuItem>
            {visibleStages.map((status) => (
              <MenuItem key={status} value={status}>
                {formatAttorneyStageFilterCount(
                  status,
                  getAttorneyStageCount(status, leadsCount)
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        renderStageFilterStrip(
          <>
            <AppButton
              variant={active ? "outlined" : "contained"}
              color="darkGray"
              size="small"
              onClick={() => handleChangeFilter(null, "AttorneyStatus")}
            >
              All({totalAttorneyLeads})
            </AppButton>
            {visibleStages.map((status) => (
              <StageStatusChip
                key={status}
                status={status}
                label={formatAttorneyStageFilterCount(
                  status,
                  getAttorneyStageCount(status, leadsCount)
                )}
                selected={isStageSelected(status, "AttorneyStatus")}
                onClick={() => handleChangeFilter(status, "AttorneyStatus")}
              />
            ))}
          </>
        )
      )}
    </>
  );
  };

  const generateTableActions = () => {
    const totalCountForAll = Object.values(leadsCount ?? {}).reduce(
      (acc, count) => acc + count,
      0
    );

    switch (props.tableType) {
      case LeadTableType.ACTIVE_CLIENT: {
        return renderStageFilterStrip(
          <>
            <AppButton
              variant={isStageSelected(null) ? "contained" : "outlined"}
              color="darkGray"
              size="small"
              onClick={() => handleChangeFilter()}
            >
              All({totalCountForAll})
            </AppButton>
            <StageStatusChip
              status={AdminLeadStatus.NEW}
              label={`Approved(${leadsCount?.new ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.NEW)}
              onClick={() => handleChangeFilter(AdminLeadStatus.NEW)}
            />
            <StageStatusChip
              status={AdminLeadStatus.CONTRACT_SENT}
              label={`Contract sent(${leadsCount?.contractSent ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.CONTRACT_SENT)}
              onClick={() => handleChangeFilter(AdminLeadStatus.CONTRACT_SENT)}
            />
            <StageStatusChip
              status={AdminLeadStatus.CONTRACT_SIGNED}
              label={`Contract signed(${leadsCount?.contractSigned ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.CONTRACT_SIGNED)}
              onClick={() => handleChangeFilter(AdminLeadStatus.CONTRACT_SIGNED)}
            />
            {(user.roles?.includes(ApplicationRole.ADMIN) ||
              (user.roles?.includes(ApplicationRole.STAFF) &&
                user.practiceArea === Category.EMPLOYMENT)) && (
              <StageStatusChip
                status={AdminLeadStatus.SUPPLEMENTAL_ASSIGNED}
                label={`Supplemental assigned(${leadsCount?.supplementalAssigned ?? 0})`}
                selected={isStageSelected(AdminLeadStatus.SUPPLEMENTAL_ASSIGNED)}
                onClick={() =>
                  handleChangeFilter(AdminLeadStatus.SUPPLEMENTAL_ASSIGNED)
                }
              />
            )}
            <StageStatusChip
              status={AdminLeadStatus.DONE}
              label={`Done(${leadsCount?.Done ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.DONE)}
              onClick={() => handleChangeFilter(AdminLeadStatus.DONE)}
            />
          </>
        );
      }
      case LeadTableType.ARCHIVE: {
        return renderStageFilterStrip(
          <>
            <AppButton
              variant={isStageSelected(null) ? "contained" : "outlined"}
              color="darkGray"
              size="small"
              onClick={() => handleChangeFilter()}
            >
              All({totalCountForAll})
            </AppButton>
            <StageStatusChip
              status={AdminLeadStatus.CLIENT_RETAINED}
              label={`Client retained(${leadsCount?.clientRetained ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.CLIENT_RETAINED)}
              onClick={() => handleChangeFilter(AdminLeadStatus.CLIENT_RETAINED)}
            />
            <StageStatusChip
              status={AdminLeadStatus.CLIENT_REFERED}
              label={`Client referred(${leadsCount?.clientRefered ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.CLIENT_REFERED)}
              onClick={() => handleChangeFilter(AdminLeadStatus.CLIENT_REFERED)}
            />
            <StageStatusChip
              status={AdminLeadStatus.DECLINED}
              label={`Declined(${leadsCount?.csaDeclined ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.DECLINED)}
              onClick={() => handleChangeFilter(AdminLeadStatus.DECLINED)}
            />
            <StageStatusChip
              status={AdminLeadStatus.NGC}
              label={`NGC(${leadsCount?.ngc ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.NGC)}
              onClick={() => handleChangeFilter(AdminLeadStatus.NGC)}
            />
            <StageStatusChip
              status={AdminLeadStatus.N_A}
              label={`N/A(${leadsCount?.n_A ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.N_A)}
              onClick={() => handleChangeFilter(AdminLeadStatus.N_A)}
            />
            <StageStatusChip
              status={AdminLeadStatus.CSADECLINED}
              label={`CSA Declined(${leadsCount?.csaDeclined ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.CSADECLINED)}
              onClick={() => handleChangeFilter(AdminLeadStatus.CSADECLINED)}
            />
            <StageStatusChip
              status={AdminLeadStatus.ATTORNEY_REJECT}
              label={`Attorney Reject(${leadsCount?.attorneyReject ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.ATTORNEY_REJECT)}
              onClick={() => handleChangeFilter(AdminLeadStatus.ATTORNEY_REJECT)}
            />
          </>
        );
      }
      case LeadTableType.REFERRAL: {
        return renderStageFilterStrip(
          <>
            <AppButton
              variant={isStageSelected(null) ? "contained" : "outlined"}
              color="darkGray"
              size="small"
              onClick={() => handleChangeFilter()}
            >
              All({totalCountForAll})
            </AppButton>
            <StageStatusChip
              status={AdminLeadStatus.NEW}
              label={`New(${leadsCount?.new ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.NEW)}
              onClick={() => handleChangeFilter(AdminLeadStatus.NEW)}
            />
            <StageStatusChip
              status={AdminLeadStatus.SHARED}
              label={`Shared(${leadsCount?.shared ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.SHARED)}
              onClick={() => handleChangeFilter(AdminLeadStatus.SHARED)}
            />
            <StageStatusChip
              status={AdminLeadStatus.WINNER_SELECTED}
              label={`Winner selected(${leadsCount?.winnerSelected ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.WINNER_SELECTED)}
              onClick={() => handleChangeFilter(AdminLeadStatus.WINNER_SELECTED)}
            />
            <StageStatusChip
              status={AdminLeadStatus.SENT_FOR_SIGNING}
              label={`Sent for signing(${leadsCount?.sentForSigning ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.SENT_FOR_SIGNING)}
              onClick={() => handleChangeFilter(AdminLeadStatus.SENT_FOR_SIGNING)}
            />
            <StageStatusChip
              status={AdminLeadStatus.SIGNED}
              label={`Agreement signed(${leadsCount?.signed ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.SIGNED)}
              onClick={() => handleChangeFilter(AdminLeadStatus.SIGNED)}
            />
            <StageStatusChip
              status={AdminLeadStatus.PAID}
              label={`Paid(${leadsCount?.paid ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.PAID)}
              onClick={() => handleChangeFilter(AdminLeadStatus.PAID)}
            />
            <StageStatusChip
              status={AdminLeadStatus.REJECTED}
              label={`Rejected(${leadsCount?.rejected ?? 0})`}
              selected={isStageSelected(AdminLeadStatus.REJECTED)}
              onClick={() => handleChangeFilter(AdminLeadStatus.REJECTED)}
            />
          </>
        );
      }
      case LeadTableType.SHARED_ATTORNEY: {
        const totalAttorneyLeads = Object.values(leadsCount ?? {}).reduce(
          (acc, count) => acc + count,
          0
        );

        return (
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{ mb: 2 }}
          >
            {renderAttorneyFilter()}
            <Box sx={{ flex: 1, minWidth: 0, "& .stage-filter-strip": { mb: 0 } }}>
              {renderAttorneyStageFilters(totalAttorneyLeads)}
            </Box>
          </Stack>
        );
      }
      default: {
        return null;
      }
    }
  };

  const generateTitleAction = () => {
    switch (props.tableType) {
      case LeadTableType.INTAKE: {
        return (
          <AppButton
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNewLead}
          >
            Add new lead
          </AppButton>
        );
      }
      case LeadTableType.SHARED_ATTORNEY:
      case LeadTableType.REFERRAL: {
        return (
          <AppButton
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            disabled={!bulkEnabled}
            onClick={() => handleSendMultipleClick(selected)}
          >
            {selected.length > 1 ? "Send Multiple Leads" : "Send Lead"}
          </AppButton>
        );
      }
      case LeadTableType.ACTIVE_CLIENT: {
        if (!isBulkArchiveMode()) {
          return null;
        }

        return (
          <AppButton
            variant="contained"
            color="primary"
            disabled={!selected.length}
            onClick={handleBulkArchiveClick}
          >
            Archive selected ({selected.length})
          </AppButton>
        );
      }
      case LeadTableType.ARCHIVE: {
        return (
          <>
            {isAttorneyRejectFilterActive() && (
              <AppButton
                variant="contained"
                color="secondary"
                disabled={!canBulkApproveAttorneyReject()}
                onClick={handleApproveAttorneyRejectBulk}
              >
                Approve Attorney Reject
              </AppButton>
            )}
            <AppButton
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              disabled={!selected || !selected.length}
              onClick={() => handleMoveToReferClick(selected)}
            >
              Move to Refer
            </AppButton>
          </>
        );
      }
      default:
        return null;
    }
  };

  function handleSendMultipleClick(selectedRows: readonly number[]) {
    navigate(`/lead/send/${selectedRows}`);
  }

  function handleMoveToReferClick(selectedRows: readonly number[]) {
    setOpenMoveToReferDialog(true);
  }

  const canBulkApproveAttorneyReject = () => {
    if (!selected?.length) return false;

    const selectedRows = visibleRows.filter((row) => selected.includes(row.id));
    return (
      selectedRows.length > 0 &&
      selectedRows.every((row) => row.status === AdminLeadStatus.ATTORNEY_REJECT)
    );
  };

  const isAttorneyRejectFilterActive = () => {
    const statusFilter = (tableInteractions.filters ?? []).find(
      (filter) => filter.filterField === "Status"
    );

    return (
      statusFilter?.filterValue?.toString().toLowerCase() ===
      AdminLeadStatus.ATTORNEY_REJECT.toLowerCase()
    );
  };

  const handleApproveAttorneyRejectBulk = () => {
    if (!canBulkApproveAttorneyReject()) {
      return;
    }

    MySwal.fire({
      title: <strong>Approve attorney reject</strong>,
      html: (
        <div>
          Approve attorney reject for {selected.length} selected{" "}
          {selected.length > 1 ? "leads" : "lead"}?
        </div>
      ),
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "No, go back",
      confirmButtonText: "Yes, approve",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) return;

      dispatch(startSpinner());
      const approvalRequest = {
        attorneyApprovalStatus: AttorneyApprovalStatus.APPROVE_REJECT,
      } as UpdateApprovalStatusRequest;

      Promise.all(
        selected.map((leadId) =>
          LeadsService.postApiLeadsApprovalChange(leadId, approvalRequest)
        )
      )
        .then(() => {
          setSelected([]);
          toast.success("Attorney reject approved for selected leads.");
          window.location.reload();
        })
        .catch(() => {
          toast.error(errorInstructions || "Error updating client approval");
        })
        .finally(() => dispatch(stopSpinner()));
    });
  };

  const getArchiveDialogCategory = (): Category | undefined => {
    if (isBulkArchive) {
      const categories = [
        ...new Set(bulkArchiveLeads.map((lead) => lead.category).filter(Boolean)),
      ];
      return categories.length === 1 ? categories[0] : undefined;
    }

    return actionRow?.category;
  };

  const requiresSameCategoryForArchive = () =>
    selectedArchiveType === AdminLeadStatus.N_A ||
    selectedArchiveType === AdminLeadStatus.NGC;

  const canSubmitBulkArchive = () => {
    if (!isBulkArchive || !requiresSameCategoryForArchive()) {
      return true;
    }

    const categories = [
      ...new Set(bulkArchiveLeads.map((lead) => lead.category).filter(Boolean)),
    ];
    return categories.length === 1;
  };

  const buildArchiveRequest = (category?: Category): ArchiveRequest => {
    return {
      status: selectedArchiveType,
      comment: archivedComment,
      reasonForNAEmp:
        category === Category.EMPLOYMENT && naReason
          ? ReasonForNAEmp[naReason]
          : null,
      reasonForNAPI:
        category === Category.PERSONAL_INJURY && naReason
          ? ReasonForNAPI[naReason]
          : null,
      reasonForNAWC:
        category === Category.WORKERS_COMPENSATION && naReason
          ? ReasonForNgcWC[naReason]
          : null,
    } as ArchiveRequest;
  };

  const handleBulkArchiveConfirm = () => {
    const archiveTypeLabel = HelpersService.camelToString(selectedArchiveType);
    const leadCount = selected.length;

    MySwal.fire({
      title: <strong>Confirm bulk archive</strong>,
      html: (
        <div>
          Archive {leadCount} selected {leadCount > 1 ? "leads" : "lead"} as{" "}
          {archiveTypeLabel}?
        </div>
      ),
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "No, go back",
      confirmButtonText: "Yes, archive",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      dispatch(startSpinner());
      const category = getArchiveDialogCategory();
      const archiveRequest = buildArchiveRequest(category);
      const bulkRequest = {
        leadIds: selected,
        status: archiveRequest.status,
        comment: archiveRequest.comment,
        reasonForNAEmp: archiveRequest.reasonForNAEmp,
        reasonForNAPI: archiveRequest.reasonForNAPI,
        reasonForNAWC: archiveRequest.reasonForNAWC,
      } as ArchiveInBulkRequest;

      LeadsService.postApiLeadsArchiveInBulk(bulkRequest)
        .then((response) => {
          setSelected([]);
          setIsBulkArchive(false);
          setBulkArchiveLeads([]);
          initiateLeads();

          const successCount = response.successCount ?? 0;
          const failureCount = response.failureCount ?? 0;

          if (failureCount === 0) {
            toast.success(
              `${successCount} ${successCount === 1 ? "lead" : "leads"} archived successfully.`
            );
            return;
          }

          if (successCount === 0) {
            toast.error("None of the selected leads could be archived.");
            return;
          }

          toast.warning(
            `${successCount} ${successCount === 1 ? "lead" : "leads"} archived successfully. ${failureCount} ${failureCount === 1 ? "lead" : "leads"} failed.`
          );
        })
        .catch(() => {
          toast.error(errorInstructions || "Error archiving leads");
        })
        .finally(() => {
          setOpenArchivedDialog(false);
          dispatch(stopSpinner());
        });
    });
  };

  const handleArchiveDialogClose = (success) => {
    if (!success) {
      setOpenArchivedDialog(false);
      setIsBulkArchive(false);
      setBulkArchiveLeads([]);
      return;
    }

    if (isBulkArchive) {
      handleBulkArchiveConfirm();
      return;
    }

    if (!actionRow) {
      setOpenArchivedDialog(false);
      return;
    }

    dispatch(startSpinner());
    const updateRequest = buildArchiveRequest(actionRow.category);
    LeadsService.postApiLeadsArchive(actionRow.id, updateRequest)
      .then(() => {
        initiateLeads();
        toast.success("Lead archived!");
      })
      .catch(() => {
        toast.error(errorInstructions || "Error archiving lead");
      })
      .finally(() => {
        setOpenArchivedDialog(false);
        dispatch(stopSpinner());
      });
  };

  const renderArchivedReasons = (category) => {
    switch (selectedArchiveType) {
      case AdminLeadStatus.N_A:
      case AdminLeadStatus.NGC: {
        return category === Category.EMPLOYMENT ? (
          <FormControl fullWidth>
            <InputLabel id="selected-label">Select reason*</InputLabel>
            <Select
              labelId="selected-label"
              label="Select reason"
              value={naReason}
              key={naReason}
              onChange={(e) => setNaReason(e.target.value as ReasonForNAEmp)}
            >
              {Object.keys(ReasonForNAEmp).map((reason) => {
                return (
                  <MenuItem key={reason} value={reason}>
                    {ReasonForNAEmpDisplayMapping[ReasonForNAEmp[reason]]}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        ) : (
          <FormControl fullWidth>
            <InputLabel id="selected-label">Select reason*</InputLabel>
            <Select
              labelId="selected-label"
              label="Select reason"
              value={naReason}
              key={naReason}
              onChange={(e) => setNaReason(e.target.value as ReasonForNAPI)}
            >
              {Object.keys(ReasonForNAPI).map((reason) => {
                return (
                  <MenuItem key={reason} value={reason}>
                    {ReasonForPIEmpDisplayMapping[ReasonForNAPI[reason]]}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        );
      }

      case AdminLeadStatus.CSADECLINED:
        return (
          <div className="col-12 mt-3">
            <TextField
              required
              fullWidth
              label="Comment"
              variant="outlined"
              value={archivedComment}
              onChange={(e) => setArchivedComment(e.target.value)}
            />
          </div>
        );
    }
  };

  const handleClientDenial = (row: LeadData) => {
    MySwal.fire({
      title: <strong>Action confirmation</strong>,
      html: <div>Are you sure you want to refer this client?</div>,
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "No, go back",
      confirmButtonText: "Yes, confirm",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(startSpinner());
        var request = {
          success: false,
          declinedAction: DeclinedAction.REFER,
        } as ClientRepresentationRequest;
        LeadsService.postApiLeadsCsaAction(row.id, request)
          .then(() => {
            toast.success("Client referred successfully.");
            navigate("/active-clients");
          })
          .catch(() => {
            toast.error(errorInstructions || "Error referring client.");
          })
          .finally(() => dispatch(stopSpinner()));
      }
    });
  };

  const handleReassignDialogClose = (success) => {
    if (!success || !actionRow) {
      setOpenReassignDialog(false);
      return;
    }

    dispatch(startSpinner()); LeadsService.postApiLeadsAssign(actionRow.id, selectedStaffAttorneyId)
      .then(() => {
        initiateLeads();
        toast.success("Lead reassigned!");
      })
      .catch(() => {
        toast.error(errorInstructions || "Error reassigning lead");
      })
      .finally(() => { setOpenArchivedDialog(false); dispatch(stopSpinner()); });
  };

  const handleMoveToReferDialogClose = (success) => {
    if (!success || !selected) {
      setOpenMoveToReferDialog(false);
      return;
    }

    const request = {
      leadIds: selected!,
      attorneyId: selectedReferStaffAttorneyId!,
      csaType: selectedCSA ? CSAType[selectedCSA] : null,
      csaComment: CSAComment
    } as MoveToReferInBulkRequest

    // selected
    dispatch(startSpinner()); 
    LeadsService.postApiLeadsMoveToReferInBulk(request)
      .then(() => {
        initiateLeads();
        toast.success("Lead moved to refer!");
      })
      .catch(() => {
        toast.error(errorInstructions || "Error moving to refer");
      })
      .finally(() => { setOpenMoveToReferDialog(false); dispatch(stopSpinner()); });
  };

  const isSharedAttorney =
  props.tableType === LeadTableType.SHARED_ATTORNEY;

  return (
    <Box sx={{ width: "100%" }}>
      <PageHeader
        title={generateTitle()}
        subtitle={generateSubtitle()}
        actions={generateTitleAction()}
      />

      <Box sx={{ mb: 2 }}>
        <LeadsTableFilters
          filters={leadsFilters}
          searchValue={nameSearchValue ?? ""}
          onSearchChange={updateSearch}
          onApply={applyLeadsFilters}
          onClear={clearLeadsFilters}
          onCreatorsLoaded={setLeadCreators}
          onReviewingAttorneysLoaded={setReviewingAttorneys}
          hideCreator={isSharedAttorney}
          hideSearch={isSharedAttorney}
        />
      </Box>

      {generateTableActions()}

      <AppTable
        columns={mapLeadHeadCells(generateTableHeadCells())}
        showRowNumber
        leadingHeadCell={
          isBulkArchiveMode() ||
          props.tableType === LeadTableType.REFERRAL ||
          props.tableType === LeadTableType.ARCHIVE ||
          props.tableType === LeadTableType.SHARED_ATTORNEY ? (
            <LeadsSelectAllHeadCell
              numSelected={selected.length}
              rowCount={isBulkArchiveMode() ? totalCount : visibleRows.length}
              onSelectAllClick={handleSelectAllClick}
            />
          ) : undefined
        }
        trailingHeadCells={
          !(props.tableType === LeadTableType.SHARED_ATTORNEY && isMobile) ? (
            <LeadsActionsHeadCells />
          ) : undefined
        }
        sortBy={tableInteractions.sortBy}
        sortAsc={tableInteractions.sortAsc}
        onRequestSort={(columnId) =>
          handleRequestSort(
            {} as React.MouseEvent<unknown>,
            columnId as keyof LeadShortResponse
          )
        }
        isEmpty={visibleRows.length === 0}
        emptyTitle="No leads"
        emptyDescription="Try adjusting filters or search."
        extraColumnCount={
          (isBulkArchiveMode() ||
          props.tableType === LeadTableType.REFERRAL ||
          props.tableType === LeadTableType.ARCHIVE ||
          props.tableType === LeadTableType.SHARED_ATTORNEY
            ? 1
            : 0) +
          (props.tableType === LeadTableType.SHARED_ATTORNEY && isMobile ? 0 : 2)
        }
        pagination={{
          count: totalCount,
          page: tableInteractions.page,
          rowsPerPage: tableInteractions.rowsPerPage,
          onPageChange: (page) =>
            setTableInteractions((prev) => ({ ...prev, page })),
          onRowsPerPageChange: (rowsPerPage) =>
            setTableInteractions((prev) => ({
              ...prev,
              rowsPerPage,
              page: 0,
            })),
        }}
      >
        {visibleRows.map((row, index) => generateTableRow(row, index))}
      </AppTable>
      <AppDialog
        open={openArchivedDialog}
        onClose={() => handleArchiveDialogClose(false)}
        title="Archive lead"
        actions={
          <>
            <AppButton
              color="gray"
              variant="contained"
              onClick={() => handleArchiveDialogClose(false)}
            >
              Cancel
            </AppButton>
            <AppButton
              disabled={
                !selectedArchiveType ||
                (selectedArchiveType !== AdminLeadStatus.CLIENT_RETAINED &&
                  !archivedComment &&
                  !naReason) ||
                (isBulkArchive && !canSubmitBulkArchive())
              }
              color="primary"
              variant="contained"
              onClick={() => handleArchiveDialogClose(true)}
            >
              Archive
            </AppButton>
          </>
        }
      >
        <div className="col-12 mt-3 mb-3">
          {isBulkArchive && (
            <div className="mb-3">
              Archiving {selected.length} selected{" "}
              {selected.length > 1 ? "leads" : "lead"}.
            </div>
          )}
          <FormControl fullWidth>
            <InputLabel id="selected-label">Select type*</InputLabel>
            <Select
              labelId="selected-label"
              name="selectedReason"
              label="Select reason"
              value={selectedArchiveType}
              key={selectedArchiveType}
              onChange={(e) =>
                setSelectedArchiveType(e.target.value as AdminLeadStatus)
              }
            >
              <MenuItem key={AdminLeadStatus.NGC} value={AdminLeadStatus.NGC}>
                NGC
              </MenuItem>
              <MenuItem key={AdminLeadStatus.N_A} value={AdminLeadStatus.N_A}>
                N/A
              </MenuItem>
              <MenuItem
                key={AdminLeadStatus.CSADECLINED}
                value={AdminLeadStatus.CSADECLINED}
              >
                CSA Declined
              </MenuItem>
              <MenuItem
                key={AdminLeadStatus.CLIENT_RETAINED}
                value={AdminLeadStatus.CLIENT_RETAINED}
              >
                Client Retained
              </MenuItem>
            </Select>
            {!!getArchiveDialogCategory() &&
              renderArchivedReasons(getArchiveDialogCategory())}
            {isBulkArchive &&
              requiresSameCategoryForArchive() &&
              !canSubmitBulkArchive() && (
                <div className="mt-3 text-danger">
                  Selected leads must share the same category to archive with
                  this type.
                </div>
              )}
          </FormControl>
        </div>
      </AppDialog>
      <AppDialog
        open={openReassignDialog}
        onClose={() => handleReassignDialogClose(false)}
        title="Reassign attorney"
        actions={
          <>
            <AppButton
              color="gray"
              variant="contained"
              onClick={() => handleReassignDialogClose(false)}
            >
              Cancel
            </AppButton>
            <AppButton
              disabled={!selectedStaffAttorneyId}
              color="primary"
              variant="contained"
              onClick={() => handleReassignDialogClose(true)}
            >
              Reassign
            </AppButton>
          </>
        }
      >
        <div className="col-12 mt-3">
          <FormControl>
            <InputLabel id="selected-label">Select attorney*</InputLabel>
            <Select
              labelId="selected-label"
              className="min-width-200"
              label="Select attorney*"
              value={selectedStaffAttorneyId}
              key={selectedStaffAttorneyId}
              onChange={(e) => setSelectedStaffAttorneyId(+e.target.value)}
            >
              {staffAttorneysList.map((attorney) => {
                return (
                  <MenuItem key={attorney.id} value={attorney.id}>
                    {attorney.firstName} {attorney.lastName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      </AppDialog>
      <AppDialog
        open={openMoveToReferDialog}
        onClose={() => handleReassignDialogClose(false)}
        title="Move to refer"
        actions={
          <>
            <AppButton
              color="gray"
              variant="contained"
              onClick={() => handleMoveToReferDialogClose(false)}
            >
              Cancel
            </AppButton>
            <AppButton
              disabled={!selectedReferStaffAttorneyId || !selectedCSA}
              color="primary"
              variant="contained"
              onClick={() => handleMoveToReferDialogClose(true)}
            >
              Move To Refer
            </AppButton>
          </>
        }
      >
        <div className="col-12 mt-3">
          <FormControl>
            <InputLabel id="selected-label">Select attorney*</InputLabel>
            <Select
              labelId="selected-label"
              className="min-width-200"
              label="Select attorney*"
              value={selectedReferStaffAttorneyId}
              key={selectedReferStaffAttorneyId}
              onChange={(e) => setSelectedReferStaffAttorneyId(+e.target.value)}
            >
              {staffAttorneysList.map((attorney) => {
                return (
                  <MenuItem key={attorney.id} value={attorney.id}>
                    {attorney.firstName} {attorney.lastName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
        <div className="col-12 mt-3">
          <FormControl>
            <InputLabel id="selected-label">
              Select CSA type*
            </InputLabel>
            <Select
              labelId="selected-label"
              name="selectedCSA"
              className="min-width-200"
              label="Select CSA type"
              value={selectedCSA}
              key={selectedCSA}
              onChange={(e) =>
                setSelectedCSA(e.target.value as CSAType)
              }
            >
              {Object.keys(CSAType).map((reason) => {
                return (
                  <MenuItem key={reason} value={reason}>
                    {CSADisplayText[CSAType[reason]]}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
        
        <div className="col-12 mt-3">
          <TextField
            label="Comment"
            multiline
            variant="outlined"
            value={CSAComment}
            onChange={(e) =>
              setCSAComment(e.target.value)
            }
          />
        </div>
      </AppDialog>
    </Box>
  );
}
